// Reads the menu snapshot (vantero-backend docs/MENU_SNAPSHOT_API.md). Matching rules
// live on the server; this only filters what the snapshot already computed.

import {
  isVipItemType,
  normalizeCategory,
  normalizeMealPeriod,
  normalizeFields,
  splitCardFields
} from "./menu"
import { isDietGroup } from "./allergens"

export const SNAPSHOT_SCHEMA_VERSION = 1

const LISTING_KINDS = new Set(["food", "wine", "bar", "spirit"])

// Checks the parts every screen reads; anything missing makes the download unusable.
export function validateSnapshot(raw) {
  if (!raw || typeof raw !== "object") return { ok: false, reason: "shape" }
  if (raw.schema_version !== SNAPSHOT_SCHEMA_VERSION) return { ok: false, reason: "schema" }

  const hasArrays = ["meal_periods", "bar_categories", "spirit_categories", "listings"].every((key) =>
    Array.isArray(raw[key])
  )
  if (!hasArrays) return { ok: false, reason: "shape" }

  const hasListings = raw.listings.every(
    (listing) =>
      listing &&
      typeof listing.meal_period === "string" &&
      typeof listing.category === "string" &&
      Array.isArray(listing.items)
  )
  if (!hasListings) return { ok: false, reason: "shape" }

  const allergen = raw.search?.allergen
  const hasSearch =
    Array.isArray(raw.search?.text) &&
    Array.isArray(allergen?.groups) &&
    Array.isArray(allergen?.corpus) &&
    Boolean(allergen?.matches) &&
    typeof allergen.matches === "object"
  if (!hasSearch) return { ok: false, reason: "shape" }

  return { ok: true }
}

export function listingKey(mealPeriodName, categoryName) {
  return `${mealPeriodName}\u0000${categoryName}`
}

// Builds the lookups the screens need once per snapshot version.
export function indexSnapshot(raw) {
  const listings = new Map()
  const itemsById = new Map()

  for (const listing of raw.listings) {
    const key = listingKey(listing.meal_period, listing.category)
    if (listings.has(key)) continue
    const items = listing.items.filter((item) => item && typeof item === "object")
    listings.set(key, {
      kind: LISTING_KINDS.has(listing.kind) ? listing.kind : "food",
      mealPeriod: listing.meal_period,
      category: listing.category,
      // The server could not build this listing, so it is empty for that reason alone.
      hasError: listing.error === true,
      items
    })
    for (const item of items) {
      const id = item.id === null || item.id === undefined ? "" : String(item.id)
      if (id && !itemsById.has(id)) itemsById.set(id, item)
    }
  }

  const { groups, corpus, matches } = raw.search.allergen
  const allergenMatches = new Map()
  for (const group of groups) {
    const ids = Array.isArray(matches[group]) ? matches[group] : []
    allergenMatches.set(String(group).trim().toLowerCase(), new Set(ids.map(String)))
  }

  return {
    generatedAt: raw.generated_at ?? null,
    restaurantId: raw.restaurant_id ?? null,
    language: raw.language ?? null,
    mealPeriods: raw.meal_periods.map(normalizeMealPeriod).filter((period) => period.name),
    barCategories: raw.bar_categories.map(normalizeCategory).filter((category) => category.name),
    spiritCategories: raw.spirit_categories.map(normalizeCategory).filter((category) => category.name),
    listings,
    itemsById,
    itemCount: itemsById.size,
    searchText: raw.search.text
      .filter(
        (entry) => entry && entry.id !== null && entry.id !== undefined && typeof entry.text === "string"
      )
      .map((entry) => ({ id: String(entry.id), text: entry.text })),
    allergenGroups: groups.map(String),
    allergenCorpus: corpus.map(String),
    allergenMatches,
    flagReasons: Array.isArray(raw.flag_reasons) ? raw.flag_reasons : [],
    // Null when the backend predates per-scope reasons
    flagReasonsByScope:
      raw.flag_reasons_by_scope && typeof raw.flag_reasons_by_scope === "object"
        ? raw.flag_reasons_by_scope
        : null,
    raw
  }
}

export const FLAG_SCOPE = { MENU_ITEM: "menu_item", VIP: "vip" }

// VIPs are flagged with guest reasons
export function flagScopeFor(item) {
  return isVipItemType(item?.itemType) ? FLAG_SCOPE.VIP : FLAG_SCOPE.MENU_ITEM
}

// The reasons a flag on this scope offers; a snapshot without per-scope reasons offers flag_reasons.
export function flagReasonsFor(menu, scope) {
  if (!menu?.flagReasonsByScope) return menu?.flagReasons ?? []
  const scoped = menu.flagReasonsByScope[scope]
  return Array.isArray(scoped) ? scoped : []
}

export function listingFor(menu, mealPeriodName, categoryName) {
  return menu?.listings.get(listingKey(mealPeriodName, categoryName)) ?? null
}

// Exactly deep_search over the snapshot items: a lowercase substring of the server's flattened text.
export function textSearch(menu, term) {
  const needle = String(term ?? "").toLowerCase()
  if (!menu || !needle) return []
  const results = []
  for (const entry of menu.searchText) {
    if (!entry.text.includes(needle)) continue
    const item = menu.itemsById.get(entry.id)
    if (item) results.push(item)
  }
  return results
}

// Allergen filters always show; a diet only when the server computed its group (older ones do not).
export function isFilterAvailable(menu, group) {
  if (!isDietGroup(group)) return true
  return Boolean(menu?.allergenMatches.has(String(group).trim().toLowerCase()))
}

// Items containing any selected group; a multi-group selection is the union of single-group matches.
export function allergenMembership(menu, groups) {
  const hit = new Set()
  if (!menu) return hit
  for (const group of groups ?? []) {
    const ids = menu.allergenMatches.get(String(group).trim().toLowerCase())
    if (ids) ids.forEach((id) => hit.add(id))
  }
  return hit
}

export function allergenSearch(menu, groups) {
  const hit = allergenMembership(menu, groups)
  const including = []
  const excluding = []
  for (const id of menu?.allergenCorpus ?? []) {
    const item = menu.itemsById.get(id)
    if (!item) continue
    if (hit.has(id)) including.push(item)
    else excluding.push(item)
  }
  return { including, excluding }
}

// The listing allergen filter: keep items in the hit set, or the ones outside it.
export function filterByAllergens(items, hit, withAllergens) {
  return items.filter((item) => hit.has(String(item?.id)) === withAllergens)
}

// Every thumbnail the menu can show, for saving ahead of going offline.
export function snapshotThumbnailUrls(menu) {
  const urls = new Set()
  const add = (value) => {
    if (typeof value === "string" && value) urls.add(value)
  }
  for (const item of menu.itemsById.values()) {
    add(item.image_thumbnail_url)
    const didYouKnow = Array.isArray(item.did_you_know) ? item.did_you_know : []
    didYouKnow.forEach((entry) => add(entry?.image?.thumbnail_url))
    const { cardFields } = splitCardFields(normalizeFields(item.data_to_display))
    cardFields.forEach((field) => field.cards.forEach((card) => add(card.thumbnailUrl)))
  }
  menu.raw.spirit_categories.forEach((category) => add(category?.image_thumbnail_url))
  return [...urls]
}
